import { Component } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'

/**
 * Production-grade error boundary.
 *
 * Catches uncaught render errors anywhere below it in the tree and renders
 * a calm, branded fallback instead of a white screen. Gives the user two
 * recovery actions: hard-refresh, or return to the home page.
 *
 * In dev, the full stack trace is still shown in the console (React forwards
 * it). In prod we hide stack details from the user but still log them.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Centralised place to forward to Sentry/Logflare later.
    // eslint-disable-next-line no-console
    console.error('[Radiance] Uncaught render error:', error, info?.componentStack)
  }

  handleReload = () => {
    window.location.reload()
  }

  handleHome = () => {
    // Hard-navigate so the broken state is fully discarded.
    window.location.href = '/'
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="grid min-h-screen place-items-center bg-paper px-6">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 ring-1 ring-amber-200">
            <AlertTriangle className="h-7 w-7 text-amber-600" />
          </div>
          <h1 className="h-display text-2xl font-bold text-ink-900">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-ink-500">
            The page hit an unexpected error. Your work isn't lost — visit drafts
            and patient data are stored safely. Try reloading.
          </p>

          {import.meta.env.DEV && (
            <details className="mt-5 rounded-xl bg-rose-50 px-4 py-3 text-left text-xs text-rose-700 ring-1 ring-rose-200">
              <summary className="cursor-pointer font-semibold">
                Error details (dev only)
              </summary>
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px]">
                {String(this.state.error?.stack || this.state.error)}
              </pre>
            </details>
          )}

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
            <button
              onClick={this.handleHome}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-ink-900 ring-1 ring-inset ring-ink-200 transition hover:bg-ink-50 hover:ring-ink-300"
            >
              <Home className="h-4 w-4" />
              Go to dashboard
            </button>
            <button
              onClick={this.handleReload}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-700 px-4 py-2.5 text-sm font-semibold text-cream transition hover:bg-primary-800"
            >
              <RefreshCw className="h-4 w-4" />
              Reload page
            </button>
          </div>
        </div>
      </div>
    )
  }
}
